import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Megaphone, Palette, ChevronDown, ChevronUp, ImagePlus, Type, Camera, Sparkles } from "lucide-react";
import client, { assetUrl } from "@/lib/glossyApi";

const HouseLook = () => {
  const [open, setOpen] = useState(false);
  const [look, setLook] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { client.get("/admin/settings").then((r) => setLook(r.data.house_look || "")).catch(() => {}); }, []);

  const save = async () => {
    setBusy(true);
    try { await client.patch("/admin/settings", { house_look: look }); toast.success("House Look saved."); }
    catch { toast.error("Could not save."); }
    setBusy(false);
  };

  return (
    <div className="border border-cream/25" data-testid="house-look-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-cream/5 transition-colors" data-testid="house-look-toggle">
        <span className="overline text-cream/70 flex items-center gap-2"><Palette size={14} /> House Look — style applied to photo tiles</span>
        {open ? <ChevronUp size={14} className="text-cream/50" /> : <ChevronDown size={14} className="text-cream/50" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <textarea value={look} onChange={(e) => setLook(e.target.value)} rows={4}
            className="w-full bg-transparent border border-cream/25 px-3 py-2 font-sans text-sm text-cream/90 focus:outline-none focus:border-gloss-gold transition-colors" data-testid="house-look-input" />
          <button onClick={save} disabled={busy} className="border border-cream/40 px-4 py-2 overline text-cream hover:bg-cream hover:text-ink transition-colors disabled:opacity-60" data-testid="house-look-save">
            {busy ? "Saving…" : "Save House Look"}
          </button>
        </div>
      )}
    </div>
  );
};

const Pick = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map(([k, label, Icon]) => (
      <button key={k} onClick={() => onChange(k)}
        className={`overline px-4 py-2.5 border flex items-center gap-2 transition-colors ${value === k ? "bg-cream text-ink border-cream" : "border-cream/30 text-cream/70 hover:border-cream"}`}
        data-testid={`${testid}-${k}`}>
        {Icon && <Icon size={14} />} {label}
      </button>
    ))}
  </div>
);

export const CampaignComposer = ({ onCreated }) => {
  const [bigLine, setBigLine] = useState("");
  const [smallLine, setSmallLine] = useState("");
  const [caption, setCaption] = useState("");
  const [brief, setBrief] = useState("");
  const [tileStyle, setTileStyle] = useState("caption");
  const [platform, setPlatform] = useState("instagram");
  const [visualNotes, setVisualNotes] = useState("");
  const [ownImage, setOwnImage] = useState(null);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  const draft = async () => {
    if (!brief.trim()) { toast.error("Give the writer a quick brief first."); return; }
    setBusy("draft");
    try {
      const r = await client.post("/admin/social/draft-copy", { brief: brief.trim() });
      setBigLine(r.data.big_line || "");
      setSmallLine(r.data.small_line || "");
      setCaption(r.data.caption || "");
      toast.success("Drafted — edit anything you like, then design it.");
    } catch (e) { toast.error(e?.response?.data?.detail || "The writer stumbled — try again."); }
    setBusy("");
  };

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setOwnImage({ name: f.name, data: null, loading: true });
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1800;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const cv = document.createElement("canvas");
        cv.width = Math.round(img.width * scale);
        cv.height = Math.round(img.height * scale);
        cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
        setOwnImage({ name: f.name, data: cv.toDataURL("image/jpeg", 0.92), loading: false });
        toast.success("Photo attached — it will be used exactly as it is.");
      };
      img.onerror = () => { setOwnImage(null); toast.error("That file couldn't be read as a photo — try a JPG or PNG."); };
      img.src = reader.result;
    };
    reader.onerror = () => { setOwnImage(null); toast.error("That photo couldn't be read — try again."); };
    reader.readAsDataURL(f);
  };

  const create = async () => {
    if (!bigLine.trim()) { toast.error("Give the tile its big line first."); return; }
    if (tileStyle === "photo" && ownImage?.loading) { toast.error("Your photo is still loading — one second, then press again."); return; }
    if (tileStyle === "photo" && ownImage && !ownImage.data) { toast.error("Your photo didn't attach — pick it again."); return; }
    setBusy("create");
    try {
      const r = await client.post("/admin/social/campaign", {
        big_line: bigLine.trim(),
        small_line: smallLine.trim(),
        caption: caption.trim(),
        tile_style: tileStyle,
        platforms: platform === "both" ? ["instagram", "tiktok"] : [platform],
        visual_notes: visualNotes.trim(),
        image_b64: tileStyle === "photo" ? (ownImage?.data || "") : "",
      });
      setResult(r.data);
      onCreated?.();
      if (r.data.status === "designing") {
        toast.success("Designing in the background — it lands in the rail below in under a minute.");
        [15000, 35000, 60000].forEach((ms) => setTimeout(() => onCreated?.(), ms));
      } else {
        toast.success("Designed — here it is.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "The desk couldn't finish — try again."); }
    setBusy("");
  };

  return (
    <div className="space-y-8">
      <div className="bg-ink text-cream p-6 sm:p-8 grain" data-testid="campaign-composer-panel">
        <div className="relative z-10 space-y-6">
          <p className="overline text-gloss-gold">Campaign Composer</p>

          <div className="border border-gloss-gold/40 p-4 space-y-3" data-testid="ai-writer-panel">
            <p className="overline text-gloss-gold flex items-center gap-2"><Sparkles size={14} /> The Writer — optional</p>
            <p className="font-sans text-xs text-cream/50">Write everything yourself below, or brief the writer and it drafts all three fields — you edit anything before designing. Your words always win.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input value={brief} onChange={(e) => setBrief(e.target.value)} placeholder='Brief, e.g. "launch teaser about being seen"'
                className="flex-1 bg-transparent border border-cream/30 px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-gloss-gold transition-colors" data-testid="ai-writer-brief" />
              <button onClick={draft} disabled={!!busy} className="border border-cream/40 px-5 py-2.5 overline hover:bg-cream hover:text-ink transition-colors disabled:opacity-60 flex items-center gap-2" data-testid="ai-writer-draft-btn">
                {busy === "draft" ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />} {busy === "draft" ? "Writing…" : "Draft it for me"}
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <p className="overline text-cream/50">1 · The words — used exactly as written</p>
            <input value={bigLine} onChange={(e) => setBigLine(e.target.value)} placeholder='Tile — the big line, e.g. "Be seen with style."'
              className="w-full bg-transparent border border-cream/30 px-4 py-3 font-serif text-lg focus:outline-none focus:border-gloss-gold transition-colors" data-testid="campaign-big-line" />
            <input value={smallLine} onChange={(e) => setSmallLine(e.target.value)} placeholder="Tile — smaller italic line (optional)"
              className="w-full bg-transparent border border-cream/30 px-4 py-3 font-serif italic text-sm focus:outline-none focus:border-gloss-gold transition-colors" data-testid="campaign-small-line" />
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3}
              placeholder="Caption — the long worded part under the post (optional; tile lines used if blank)"
              className="w-full bg-transparent border border-cream/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-gloss-gold transition-colors" data-testid="campaign-caption-input" />
          </div>

          <div>
            <p className="overline text-cream/50 mb-2">2 · Tile style</p>
            <Pick testid="campaign-style" value={tileStyle} onChange={setTileStyle} options={[
              ["caption", "Caption tile — black & gold", Type],
              ["photo", "Photo tile", Camera],
            ]} />
          </div>

          {tileStyle === "photo" && (
            <div className="space-y-3 border-l-2 border-gloss-gold/40 pl-4">
              <input value={visualNotes} onChange={(e) => setVisualNotes(e.target.value)}
                placeholder="Optional art direction for the shoot…"
                className="w-full bg-transparent border border-cream/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-gloss-gold transition-colors" data-testid="campaign-visual-input" />
              <div className="flex flex-wrap items-center gap-3">
                <label className="border border-cream/40 px-4 py-2.5 overline text-cream/80 hover:bg-cream hover:text-ink transition-colors cursor-pointer flex items-center gap-2" data-testid="campaign-own-image-label">
                  <ImagePlus size={14} /> {ownImage ? "Change photo" : "Use my own photo"}
                  <input type="file" accept="image/*" onChange={pickImage} className="hidden" data-testid="campaign-own-image-input" />
                </label>
                {ownImage && (
                  <span className="flex items-center gap-2 font-sans text-xs text-cream/70" data-testid="campaign-own-image-name">
                    {ownImage.loading ? <span className="text-gloss-gold animate-pulse">Loading photo…</span> : <img src={ownImage.data} alt="" className="w-9 h-9 object-cover" />} {ownImage.name}
                    <button onClick={() => setOwnImage(null)} className="text-cream/50 hover:text-red-400 transition-colors" data-testid="campaign-own-image-clear">✕</button>
                  </span>
                )}
                {!ownImage && <span className="font-sans text-xs text-cream/45">No photo? The desk shoots one in your House Look.</span>}
              </div>
              {ownImage && (
                <p className="font-sans text-xs text-cream/60" data-testid="campaign-real-photo-note">
                  Your photo is used exactly as it is — real photography, never restyled.
                </p>
              )}
            </div>
          )}

          <div>
            <p className="overline text-cream/50 mb-2">3 · Where is it for?</p>
            <Pick testid="campaign-platform" value={platform} onChange={setPlatform} options={[
              ["instagram", "Instagram"],
              ["tiktok", "TikTok"],
              ["both", "Both"],
            ]} />
          </div>

          <button onClick={create} disabled={!!busy} className="bg-cream text-ink px-6 py-3 overline flex items-center gap-2 hover:bg-gloss-gold transition-colors disabled:opacity-60" data-testid="campaign-create-btn">
            {busy === "create" ? <RefreshCw size={16} className="animate-spin" /> : <Megaphone size={16} />} {busy === "create" ? (tileStyle === "caption" ? "Designing…" : "Designing… ~1 min") : "Design it"}
          </button>

          <HouseLook />
        </div>
      </div>

      {result && result.status === "designing" && (
        <div className="border-2 border-gloss-gold/60 p-6 sm:p-8" data-testid="campaign-designing">
          <p className="overline text-gloss-gold">On the drawing board…</p>
          <p className="font-sans text-sm text-ink/60 mt-2">The desk is laying it out — your tile appears in the rail below in under a minute. No need to wait here.</p>
          <button onClick={() => setResult(null)} className="mt-3 overline text-ink/50 hover:text-ink transition-colors" data-testid="campaign-designing-close">Design another</button>
        </div>
      )}
      {result && result.status !== "designing" && (
        <div className="border-2 border-gloss-gold/60 p-6 sm:p-8" data-testid="campaign-result">
          <div className="flex items-center justify-between mb-5">
            <p className="overline text-gloss-gold">Here it is — live in your {(result.platforms || ["instagram"]).join(" & ")} desk</p>
            <button onClick={() => setResult(null)} className="overline text-ink/50 hover:text-ink transition-colors" data-testid="campaign-result-close">Design another</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-6 items-start">
            <div className="space-y-4">
              <img src={assetUrl(result.image)} alt="Tile" className="w-full max-w-md border border-ink/10" data-testid="campaign-result-tile" />
            </div>
            <div className="space-y-4 max-w-sm">
              <img src={assetUrl(result.story_image)} alt="Story" className="w-40 border border-ink/10" data-testid="campaign-result-story" />
              <div>
                <p className="overline text-ink/50 mb-1">Caption</p>
                <p className="font-sans text-sm whitespace-pre-line leading-relaxed" data-testid="campaign-result-caption">{result.caption}</p>
              </div>
              {result.hashtags?.length > 0 && <p className="font-mono text-xs text-ink/60">{result.hashtags.join(" ")}</p>}
              <p className="font-sans text-xs text-ink/45">Not right? Head to the desk card — edit the caption, post now or schedule.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
